import type {Action} from '@github-ui/action-bar'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {AddToProjectsBulkAction} from '@github-ui/issues-bulk-actions/AddToProjectsBulkAction'
import {ApplyAssigneesBulkAction} from '@github-ui/issues-bulk-actions/ApplyAssigneesBulkAction'
import {ApplyIssueTypeBulkAction} from '@github-ui/issues-bulk-actions/ApplyIssueTypeBulkAction'
import {ApplyLabelsBulkAction} from '@github-ui/issues-bulk-actions/ApplyLabelsBulkAction'
import {ApplyMilestoneBulkAction} from '@github-ui/issues-bulk-actions/ApplyMilestoneBulkAction'
import {BulkMarkAs} from '@github-ui/issues-bulk-actions/BulkMarkAs'
import {guardOverflowMenuChange} from '@github-ui/issues-react/utils/guard-overflow-menu-change'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {useListViewMultiPageSelection} from '@github-ui/list-view/ListViewMultiPageSelectionContext'
import {useListViewSelection} from '@github-ui/list-view/ListViewSelectionContext'
import {noop} from '@github-ui/noop'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  type Icon,
  IssueOpenedIcon,
  MilestoneIcon,
  PeopleIcon,
  ProjectSymlinkIcon,
  RocketIcon,
  TagIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {Suspense, useCallback, useMemo, useState} from 'react'

import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import type {ItemNodeType, ListItemsHeaderProps, ScopedRepository} from '../types'

function ListItemsHeaderBulkFallback() {
  const renderFake = useCallback((isOverflowing: boolean, LeadingVisual: Icon, anchorText: string) => {
    if (isOverflowing) {
      return (
        <ActionList.Item disabled>
          <ActionList.LeadingVisual>
            <LeadingVisual />
          </ActionList.LeadingVisual>
          {anchorText}
          <ActionList.TrailingVisual>
            <TriangleDownIcon />
          </ActionList.TrailingVisual>
        </ActionList.Item>
      )
    }

    return (
      <Button disabled leadingVisual={LeadingVisual} trailingVisual={TriangleDownIcon}>
        {anchorText}
      </Button>
    )
  }, [])

  // This array should be in sync with the actions array populating the actual component
  const actions = useMemo(
    () => [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, VALUES.issueIcons.CLOSED.icon, LABELS.markAs),
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, TagIcon, LABELS.label),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, PeopleIcon, LABELS.assign),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, ProjectSymlinkIcon, LABELS.project),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, MilestoneIcon, LABELS.milestone),
      },
      {
        key: 'apply-issue-type',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, IssueOpenedIcon, LABELS.setIssueType),
      },
    ],
    [renderFake],
  )

  return (
    <ListViewMetadata onToggleSelectAll={noop} actionsLabel={LABELS.bulkActions} actions={actions} density={'normal'} />
  )
}

export const ListItemsHeaderWithBulkActions = ({
  checkedItems,
  issueNodes,
  setCheckedItems,
  updateListHasPRs,
  useBulkActions,
  scopedRepository,
  bulkJobId,
  setBulkJobId,
  isInOrganization,
}: ListItemsHeaderProps & {scopedRepository: ScopedRepository}) => {
  const {addToast, addPersistedToast} = useToastContext()
  const {setSelectedCount} = useListViewSelection()
  const {setMultiPageSelectionAllowed} = useListViewMultiPageSelection()
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(bulkJobId !== null)
  const isIssueNode = (node: ItemNodeType): node is ItemNodeType => node.__typename === 'Issue'

  const issuesToActOn: ItemNodeType[] = Array.from(checkedItems.values())
    .filter(v => v != null)
    .filter(isIssueNode)

  const bulkActionCompleted = useCallback(
    (jobId?: string) => {
      setIsBulkActionRunning(false)
      if (jobId) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          type: 'info',
          message: 'Updating issues', //LABELS.updatingIssues,
          icon: <RocketIcon />,
          role: 'status',
        })
        setBulkJobId(jobId)
      }
    },
    [addPersistedToast, setBulkJobId],
  )

  const bulkActionError = useCallback(
    (error: Error) => {
      setIsBulkActionRunning(false)
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: `Could not update issues: ${error.message}`,
      })
    },
    [addToast],
  )

  const sharedActionProps = useMemo(
    () => ({
      onCompleted: bulkActionCompleted,
      onError: bulkActionError,
      disabled: isBulkActionRunning,
      issuesToActOn: issuesToActOn.filter(node => node != null).map(node => node.id),
    }),
    [bulkActionCompleted, bulkActionError, isBulkActionRunning, issuesToActOn],
  )

  const onToggleSelectAll = useCallback(
    (isSelectAllChecked: boolean) => {
      if (isSelectAllChecked) {
        setCheckedItems(
          issueNodes.filter(node => node != null).reduce((map, node) => map.set(node.id, node), new Map()),
        )
        updateListHasPRs(
          issueNodes.filter(node => node != null).reduce((map, node) => map.set(node.id, node), new Map()),
        )
      } else {
        setCheckedItems(new Map())
        setSelectedCount(0)
        setMultiPageSelectionAllowed?.(false)
      }
    },
    [issueNodes, setCheckedItems, setMultiPageSelectionAllowed, setSelectedCount, updateListHasPRs],
  )

  const shouldRenderBulkActions = scopedRepository && useBulkActions
  const [overflowMenuOpen, setOverflowMenuOpen] = useState<boolean | undefined>(undefined)

  const analyticsMetadata = useMemo(
    () => ({
      owner: scopedRepository?.owner ?? '',
      repositoryName: scopedRepository?.name ?? '',
    }),
    [scopedRepository?.name, scopedRepository?.owner],
  )

  const overflowMenuProps = useMemo(() => {
    // see https://github.com/github/primer/issues/5896#issuecomment-3335244847
    const onOpenChange = (open: boolean) => guardOverflowMenuChange(open, setOverflowMenuOpen)
    return {open: overflowMenuOpen, onOpenChange}
  }, [overflowMenuOpen])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const actions = useMemo<Action[] | undefined>(() => {
    if (!shouldRenderBulkActions) return
    const issueIds = issuesToActOn.map(i => i.id)
    const issueNumbers = issuesToActOn.map(i => i.number)

    const actionList = [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => <BulkMarkAs {...sharedActionProps} nested={isOverflowMenu} />,
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => (
          <ApplyLabelsBulkAction
            owner={scopedRepository.owner}
            repo={scopedRepository.name}
            nested={isOverflowMenu}
            issueIds={issueIds}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => (
          <ApplyAssigneesBulkAction
            nested={isOverflowMenu}
            issueIds={issueIds}
            issueNumbers={issueNumbers}
            {...sharedActionProps}
            owner={scopedRepository?.owner}
            repo={scopedRepository?.name}
          />
        ),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => (
          <AddToProjectsBulkAction
            nested={isOverflowMenu}
            issueIds={issueIds}
            owner={scopedRepository?.owner}
            repo={scopedRepository?.name}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => (
          <ApplyMilestoneBulkAction
            owner={scopedRepository.owner}
            repo={scopedRepository.name}
            nested={isOverflowMenu}
            issueIds={issuesToActOn.map(i => i.id)}
            {...sharedActionProps}
          />
        ),
      },
    ]

    if (isInOrganization)
      actionList.push({
        key: 'apply-issue-type',
        render: (isOverflowMenu: boolean) => (
          <AnalyticsProvider appName="issue_types" category="issues_index" metadata={analyticsMetadata}>
            <ApplyIssueTypeBulkAction
              owner={scopedRepository.owner}
              repo={scopedRepository.name}
              nested={isOverflowMenu}
              issueIds={issueIds}
              {...sharedActionProps}
            />
          </AnalyticsProvider>
        ),
      })

    return actionList
  }, [
    sharedActionProps,
    scopedRepository?.owner,
    scopedRepository?.name,
    issuesToActOn,
    shouldRenderBulkActions,
    analyticsMetadata,
    isInOrganization,
  ])

  return (
    <Suspense fallback={<ListItemsHeaderBulkFallback />}>
      <ListViewMetadata
        onToggleSelectAll={onToggleSelectAll}
        actionsLabel={LABELS.bulkActions}
        actions={actions}
        density={'normal'}
        {...sharedActionProps}
        overflowMenuProps={overflowMenuProps}
      />
    </Suspense>
  )
}
